import window from 'global';
import _ from 'lodash';
import * as React from 'react';
import { withRouter } from 'react-router';
import { setChangelogCompressedContentInLocalStorage } from '../../../utils/changelogPimple/utils';
import { transformNodeKeyToDocsUrl } from '../../../utils/flattenTree/utils';
import { fromRelativeUrlToAbsoluteUrl } from '../../../utils/locationUtils';
import { invokeOrRenderChildren } from '../../../utils/reactHelpers/index';
import {
  clearActiveTableOfContentLinks,
  isActiveSectionUnactivated,
  updateActiveTableOfContentLink,
} from '../../../utils/tableOfContentUtils';
import { clearTimeoutIfNeeded } from '../../../utils/timeout.utils';
import { wixDocsNavigationClick } from '../../LoggerFactory/BiAnalyticsLogger/events';
import {
  NAVIGATION_BI_EVENT_TIMEOUT,
  NAV_TYPES,
} from '../../LoggerFactory/BiAnalyticsLogger/events/consts';
import { getCategoryName } from '../../LoggerFactory/BiAnalyticsLogger/events/utils';
import { LOGGER_TYPES } from '../../LoggerFactory/loggerTypes';
import { withLogger } from '../../appContext/LoggerProvider';
import { connectStore } from '../../appContext/StoreProvider/ConnectStore';
import { isClientSide } from '../../../common/utils';
import { withExperiments } from '@wix/wix-experiments-react';

export const SubscriptionContext = React.createContext({
  subscribeToIntersectionObserver: (ref) => {},
  unsubscribeFromObserver: (ref) => {},
});

export const SubscriptionProvider = ({ children, value }) => (
  <SubscriptionContext.Provider value={value}>
    {children}
  </SubscriptionContext.Provider>
);

export const SubscriptionConsumer = SubscriptionContext.Consumer;

class SectionIntersectionDetection extends React.Component {
  sectionsRef = {};
  observer = null;
  biTimeoutId = null;

  throttledResizeHandler = _.throttle(() => {
    this.initializeIntersectionObserver();
    // TODO: investigate why do we need this?

    // if (this.state?.lastEntry?.target?.id) {
    //   document
    //     .querySelector(`[id="${this.state.lastEntry.target.id}"`)
    //     .scrollIntoView();
    // }
  }, 100);

  constructor(props) {
    super(props);
    this.state = {
      subscribeToIntersectionObserver: this.subscribeToIntersectionObserver,
      unsubscribeFromObserver: this.unsubscribeFromObserver,
      windowInnerHeight: isClientSide() && window.innerHeight,
      lastEntry: {},
    };
    if (isClientSide()) {
      window.addEventListener('resize', this.throttledResizeHandler);
    }
  }

  shouldComponentUpdate() {
    return !!this.props.isBot;
  }

  logScrollEvent = (categoryName, articleName) => {
    this.props.logger
      .getLogger(LOGGER_TYPES.ANALYTICS_BI)
      .log(wixDocsNavigationClick, {
        nav_type: NAV_TYPES.SCROLL,
        category_name: categoryName,
        article_name: articleName,
        article_url: fromRelativeUrlToAbsoluteUrl(
          `/${categoryName}${
            articleName ? `/${transformNodeKeyToDocsUrl(articleName)}` : ''
          }`,
          window.location.origin,
          this.props.store
        ),
      });
  };

  setNewTimeout = (entry) => {
    this.biTimeoutId = setTimeout(() => {
      const articleId = entry.target.id;
      const articleName = articleId && articleId.split('/').splice(1).join('.');

      const categoryName = getCategoryName(location.pathname, this.props.store);
      this.logScrollEvent(categoryName, articleName);
    }, NAVIGATION_BI_EVENT_TIMEOUT);
  };

  handleIntersection = (entry) => {
    const [, hash] = entry.target.id.split('#');
    const { store, experiments, onIntersection } = this.props;
    if (hash) {
      // intersection is of a heading which has a table of content link
      const shouldDisableTocScroll = experiments.enabled(
        'specs.wix-docs.DisableTableOfContentScrollIntoView'
      ); // this FT disable the Table of content scroll into view - should fix the bug of scroll jumping, discussion: https://wix.slack.com/archives/CC1U35ZRA/p1635953077044600
      updateActiveTableOfContentLink(
        hash,
        store.storePath,
        shouldDisableTocScroll
      );
    } else {
      if (entry.target.getAttribute('data-changelog-content')) {
        setChangelogCompressedContentInLocalStorage(store, entry);
      }
      clearTimeoutIfNeeded(this.biTimeoutId);
      this.setNewTimeout(entry);
      this.setState({ lastEntry: entry });
    }
    onIntersection(entry);
  };

  onSectionIntersection = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.handleIntersection(entry);
      } else if (
        isActiveSectionUnactivated(entry, this.props.store.storePath)
      ) {
        clearActiveTableOfContentLinks();
      }
    });
  };

  resizeEventOccurred = () =>
    this.state.windowInnerHeight !== window.innerHeight;

  initializeIntersectionObserver = () => {
    /*
      Basically, the rootMargin defined to be the upper 30px of the containing rectangle
      so this means every time an element intersects with the top of the root element the callback is invoked
    */
    if (!this.observer || this.resizeEventOccurred()) {
      const withNotificationPxls = this.props.withNotificationBar ? 48 : 0;
      const rootMarginBottomShrinkingPrecentage = Math.floor(
        100 - ((150 + withNotificationPxls) / window.innerHeight) * 100
      );
      const rootMargin = `0px 0px -${rootMarginBottomShrinkingPrecentage}%`;
      const options = {
        root: this.rootEl,
        rootMargin,
        threshold: [0],
      };

      this.observer = new IntersectionObserver(
        this.onSectionIntersection,
        options
      );

      Object.values(this.sectionsRef).forEach((section) =>
        this.observer.observe(section)
      );
      this.setState({ windowInnerHeight: window && window.innerHeight });
    }
  };

  subscribeToIntersectionObserver = (el) => {
    if (!this.observer) {
      this.initializeIntersectionObserver();
    }
    if (el && this.observer) {
      this.observer.observe(el);
    }
  };

  unsubscribeFromObserver = (el) => {
    if (el) {
      this.observer.unobserve(el);
    }
  };

  componentDidMount() {
    this.initializeIntersectionObserver();
  }

  componentWillUnmount() {
    clearTimeoutIfNeeded(this.biTimeoutId);
    window.removeEventListener('resize', this.throttledResizeHandler);
    this.observer && this.observer.disconnect();
  }

  render() {
    const { dataHook, children, className } = this.props;
    return (
      <div
        data-hook={dataHook}
        ref={(r) => (this.rootEl = r)}
        className={className}
      >
        <SubscriptionProvider
          value={{
            subscribeToIntersectionObserver:
              this.state.subscribeToIntersectionObserver,
            unsubscribeFromObserver: this.unsubscribeFromObserver,
          }}
        >
          {invokeOrRenderChildren({
            children,
            callWith: {
              sectionWrapper: this.sectionsRef,
              subscribeToIntersectionObserver:
                this.state.subscribeToIntersectionObserver,
              rootRef: this.rootEl,
            },
          })}
        </SubscriptionProvider>
      </div>
    );
  }
}

export default withRouter(
  withLogger(withExperiments(connectStore(SectionIntersectionDetection)))
);
